/**
 * Per-selection styling for a text object.
 *
 * A text object keeps its plain string in `text` — everything that reads the
 * box (search, export, the blank-box check, the editor's value) still works —
 * plus `marks`: sorted, non-overlapping `{ start, end, style }` spans that
 * override the object's own style over part of that string. A box with no
 * marks is exactly what it always was, so nothing had to be migrated.
 *
 * The styleable properties are the ones the painter reads per run. Position,
 * alignment and line height stay box-level: they describe the block, not a run.
 */

export const RUN_STYLE_KEYS = [
    'bold',
    'italic',
    'underline',
    'strike',
    // WordArt, as the two effects that survive a PDF: a stroked outline and a
    // drop shadow. Both are painted, never vector-set, so text wearing one is
    // stamped rather than written (see vectorText).
    'outline',
    'shadow',
    'color',
    'fontFamily',
    'fontStack',
    'fontLabel',
    'fontId',
    'fontSize',
];

/** The subset of a patch that belongs on a run rather than on the box. */
export function runStyleOf(patch = {}) {
    const out = {};
    for (const key of RUN_STYLE_KEYS) {
        if (patch[key] !== undefined) out[key] = patch[key];
    }
    return out;
}

const sameStyle = (a = {}, b = {}) => RUN_STYLE_KEYS.every((k) => a[k] === b[k]);

/** Marks sorted, empty ones dropped, neighbours with identical styles merged. */
function normalise(marks) {
    const kept = marks
        .filter((m) => m.end > m.start && Object.keys(m.style || {}).length)
        .sort((a, b) => a.start - b.start);

    const out = [];
    for (const mark of kept) {
        const last = out[out.length - 1];
        if (last && last.end === mark.start && sameStyle(last.style, mark.style)) {
            last.end = mark.end;
        } else {
            out.push({ ...mark, style: { ...mark.style } });
        }
    }
    return out;
}

/** Style of the character at `index` — the box's own style with any mark on top. */
export function styleAt(obj, index) {
    const mark = (obj.marks || []).find((m) => index >= m.start && index < m.end);
    return mark ? { ...obj, ...mark.style } : obj;
}

/**
 * The object's text split into runs of one style.
 *
 * Each run carries a full style object (the box's, overridden by its mark), so
 * the painter and the exporter can treat a run exactly like a plain text object.
 */
export function runsOf(obj) {
    const text = String(obj.text ?? '');
    const marks = (obj.marks || []).filter((m) => m.start < text.length && m.end > 0);
    if (!marks.length) return [{ text, start: 0, style: obj }];

    const runs = [];
    let at = 0;
    for (const mark of marks) {
        const start = Math.max(at, mark.start);
        const end = Math.min(text.length, mark.end);
        if (start > at) runs.push({ text: text.slice(at, start), start: at, style: obj });
        if (end > start) {
            runs.push({ text: text.slice(start, end), start, style: { ...obj, ...mark.style } });
        }
        at = Math.max(at, end);
    }
    if (at < text.length) runs.push({ text: text.slice(at), start: at, style: obj });
    return runs;
}

/**
 * Drop `keys` from every mark.
 *
 * A run's style wins over the box's (see `runsOf`), so setting the box colour
 * while every character sits under a mark carrying its own colour changes
 * nothing on screen — the swatch looked dead. Styling with the whole box
 * selected means "all of it", so the marks give those properties up and keep
 * whatever else they were carrying.
 */
export function clearMarkStyle(obj, keys) {
    const marks = obj.marks || [];
    if (!marks.length || !keys.length) return marks;
    return normalise(marks.map((mark) => {
        const style = { ...mark.style };
        for (const key of keys) delete style[key];
        return { ...mark, style };
    }));
}

/** Whether every character of the object shares the box's own style. */
export const isPlainText = (obj) => !(obj.marks || []).length;

/** Apply a style patch to `[start, end)`, splitting and merging as needed. */
export function markRange(obj, start, end, patch) {
    const style = runStyleOf(patch);
    if (!Object.keys(style).length || end <= start) return obj.marks || [];

    const out = [];
    for (const mark of obj.marks || []) {
        // The parts of an existing mark outside the new range survive unchanged.
        if (mark.start < start) out.push({ start: mark.start, end: Math.min(mark.end, start), style: mark.style });
        if (mark.end > end) out.push({ start: Math.max(mark.start, end), end: mark.end, style: mark.style });
        // The overlapping part keeps its own style with the patch layered on.
        const from = Math.max(mark.start, start);
        const to = Math.min(mark.end, end);
        if (to > from) out.push({ start: from, end: to, style: { ...mark.style, ...style } });
    }

    // Whatever the range covers that no mark did.
    const covered = out.filter((m) => m.start >= start && m.end <= end).sort((a, b) => a.start - b.start);
    let at = start;
    for (const mark of covered) {
        if (mark.start > at) out.push({ start: at, end: mark.start, style });
        at = Math.max(at, mark.end);
    }
    if (at < end) out.push({ start: at, end, style });

    return normalise(out);
}

/**
 * Move marks across an edit — `removed` characters at `at` replaced by
 * `inserted` new ones.
 *
 * Typed characters inherit the style of the character before them, which is
 * what every editor does: a mark that ends exactly at the caret grows.
 */
export function shiftMarks(marks = [], at, removed, inserted) {
    const delta = inserted - removed;
    const endOfRemoval = at + removed;
    const moved = [];

    for (const mark of marks) {
        let { start, end } = mark;
        if (start >= endOfRemoval) {
            start += delta;
            end += delta;
        } else if (end <= at) {
            // Entirely before the edit — but typing right after it extends it.
            if (end === at && inserted > 0) end += inserted;
        } else {
            // Overlaps the removal: clip to what is left, then shift the tail.
            start = start < at ? start : at;
            end = end > endOfRemoval ? end + delta : Math.max(start, at + inserted);
        }
        moved.push({ start, end, style: mark.style });
    }
    return normalise(moved);
}

/** The style the toolbar should show for a selection (or caret) inside a box. */
export function styleOfSelection(obj, start, end) {
    if (!(obj.marks || []).length) return obj;
    if (end > start) return styleAt(obj, start);
    // At a caret, report the character before it — the one typing would follow.
    return styleAt(obj, Math.max(0, start - 1));
}
